import React, { createContext, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { initTracking } from "@/api/FacebookAPI";
import { getSupportedLanguage } from "@/locales/i18n";
import { verificationService } from "../services/verificationService";

const KycSessionContext = createContext(null);

export function KycSessionProvider({ sessionId, children }) {
  const { i18n } = useTranslation();
  const query = useQuery({
    queryKey: ["verification-session", sessionId],
    queryFn: ({ signal }) => verificationService.getSession(sessionId, signal),
    enabled: Boolean(sessionId),
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      return ![400, 404, 410].includes(status) && failureCount < 1;
    },
  });

  const siteUrl = query.data?.siteUrl ?? "";
  const embeddedSiteConfig = query.data?.siteConfig ?? {};
  const trackingQuery = useQuery({
    queryKey: [
      "verification-site-config",
      sessionId,
      siteUrl,
      embeddedSiteConfig.pixel,
      embeddedSiteConfig.googlePixel,
      embeddedSiteConfig.tiktokPixel,
      embeddedSiteConfig.adPlatform,
    ],
    queryFn: () => initTracking(siteUrl, sessionId, embeddedSiteConfig),
    enabled: Boolean(query.data && siteUrl),
    retry: 1,
    staleTime: Infinity,
  });

  const sessionLocale = query.data?.locale;

  useEffect(() => {
    if (!sessionLocale) return;

    const supportedLocale = getSupportedLanguage(sessionLocale);

    if (i18n.resolvedLanguage !== supportedLocale) {
      i18n.changeLanguage(supportedLocale);
    }
  }, [i18n, sessionLocale]);

  return (
    <KycSessionContext.Provider
      value={{
        sessionId,
        session: query.data,
        siteUrl,
        siteConfig: trackingQuery.data ?? query.data?.siteConfig ?? null,
        siteConfigError: trackingQuery.error || null,
        isLoading:
          query.isLoading || (Boolean(siteUrl) && trackingQuery.isLoading),
        isError: query.isError,
        error: query.error,
        retry: query.refetch,
      }}
    >
      {children}
    </KycSessionContext.Provider>
  );
}

export function useKycSession() {
  const context = useContext(KycSessionContext);

  if (!context) {
    throw new Error("useKycSession must be used inside KycSessionProvider.");
  }

  return context;
}
